/**
 * CuidaDF - Cliente de API do Frontend
 * Modulo responsavel pela comunicacao com o backend de triagem
 *
 * Autora: Izabella Kristinne Dias Porto
 * Titular: I.P Tecnologia (ME)
 * Versao: 1.0
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

/**
 * Envia os dados da triagem coletados do usuario para o backend
 * e retorna o direcionamento (orientacao) junto com a unidade de
 * saude recomendada.
 *
 * @param {Object} dadosTriagem
 * @param {Object} dadosTriagem.respostas - Respostas das 5 perguntas de multipla escolha
 * @param {number} [dadosTriagem.latitude]
 * @param {number} [dadosTriagem.longitude]
 * @returns {Promise<Object>} Resultado com direcionamento e unidade recomendada
 */
export async function enviarTriagem(dadosTriagem) {
  const resposta = await fetch(`${API_BASE_URL}/api/triagem`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(dadosTriagem),
  });

  if (!resposta.ok) {
    throw new Error(`Erro na triagem: ${resposta.status}`);
  }

  return resposta.json();
}

/**
 * Busca o tempo de espera atual de todas as UPAs monitoradas
 * pelo painel do IgesDF, para exibicao na tela inicial. Se
 * latitude/longitude forem informadas, o backend ordena as
 * unidades por proximidade.
 *
 * @param {number} [latitude]
 * @param {number} [longitude]
 * @returns {Promise<Object>} Lista de unidades com tempo de espera
 */
export async function buscarFilasAtuais(latitude, longitude) {
  const params = new URLSearchParams();
  if (latitude != null) params.set("latitude", latitude);
  if (longitude != null) params.set("longitude", longitude);
  const query = params.toString() ? `?${params.toString()}` : "";

  const resposta = await fetch(`${API_BASE_URL}/api/unidades/filas${query}`);

  if (!resposta.ok) {
    throw new Error(`Erro ao buscar filas: ${resposta.status}`);
  }

  return resposta.json();
}

/**
 * Obtem a localizacao atual do usuario via Geolocation API do navegador,
 * usada para calcular a distancia até as unidades de saude.
 *
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export function obterLocalizacaoUsuario() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocalizacao nao suportada neste navegador"));
      return;
    }

    // Primeira tentativa: alta precisao com timeout generoso
    navigator.geolocation.getCurrentPosition(
      (posicao) => {
        resolve({
          latitude: posicao.coords.latitude,
          longitude: posicao.coords.longitude,
        });
      },
      () => {
        // Se falhar com alta precisao, tenta sem (mais rapido no iPhone)
        navigator.geolocation.getCurrentPosition(
          (posicao) => {
            resolve({
              latitude: posicao.coords.latitude,
              longitude: posicao.coords.longitude,
            });
          },
          (erro) => reject(erro),
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

export default {
  enviarTriagem,
  buscarFilasAtuais,
  obterLocalizacaoUsuario,
};